const SkeletonBox = ({ className = '' }) => (
  <div className={`bg-gray-200 animate-pulse ${className}`} />
);

const LandingPageSkeleton = () => {
  const gridItems = Array.from({ length: 8 });
  const productItems = Array.from({ length: 6 });
  const testimonialItems = Array.from({ length: 3 });

  return (
    <main
      role="status"
      aria-label="Loading landing page"
      className="space-y-8 p-6 sm:p-8 lg:p-12 xl:px-20"
    >
      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
        <div className="space-y-4">
          <SkeletonBox className="h-8 w-3/4 rounded-md" />
          <SkeletonBox className="h-5 w-2/3 rounded-md" />
          <div className="flex gap-3 mt-4">
            <SkeletonBox className="h-10 w-36 rounded-full" />
            <SkeletonBox className="h-10 w-28 rounded-full" />
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            <SkeletonBox className="h-12 rounded-md" />
            <SkeletonBox className="h-12 rounded-md" />
            <SkeletonBox className="h-12 rounded-md" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 flex items-center justify-center shadow-sm">
          <SkeletonBox className="h-60 w-full rounded-lg" />
        </div>
      </section>

      {/* Feature Pills */}
      <section className="flex flex-wrap gap-3 items-center">
        {['', '', '', ''].map((_, i) => (
          <SkeletonBox key={i} className="h-10 w-40 rounded-full" />
        ))}
      </section>

      {/* Categories / Highlights */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <SkeletonBox className="h-5 w-48 rounded" />
          <SkeletonBox className="h-4 w-24 rounded" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {gridItems.map((_, i) => (
            <div key={i} className="bg-white border rounded-lg p-4 shadow-sm">
              <SkeletonBox className="h-36 w-full rounded-md mb-3" />
              <SkeletonBox className="h-4 w-3/4 rounded mb-2" />
              <SkeletonBox className="h-3 w-1/2 rounded" />
            </div>
          ))}
        </div>
      </section>

      {/* Products / Cards */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <SkeletonBox className="h-5 w-48 rounded" />
          <SkeletonBox className="h-4 w-24 rounded" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {productItems.map((_, i) => (
            <div key={i} className="bg-white border rounded-lg p-4 shadow-sm">
              <SkeletonBox className="h-40 w-full rounded-md mb-3" />
              <SkeletonBox className="h-4 w-3/4 rounded mb-2" />
              <div className="flex items-center justify-between mt-2">
                <SkeletonBox className="h-4 w-20 rounded" />
                <SkeletonBox className="h-8 w-20 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <SkeletonBox className="h-5 w-48 rounded" />
          <SkeletonBox className="h-4 w-24 rounded" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {testimonialItems.map((_, i) => (
            <div key={i} className="bg-white border rounded-lg p-4 shadow-sm flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <SkeletonBox className="h-10 w-10 rounded-full" />
                <SkeletonBox className="h-4 w-32 rounded" />
              </div>
              <SkeletonBox className="h-3 w-full rounded" />
              <SkeletonBox className="h-3 w-5/6 rounded" />
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter / CTA */}
      <section className="bg-white border rounded-lg p-6 shadow-sm flex flex-col sm:flex-row items-center gap-4">
        <div className="flex-1">
          <SkeletonBox className="h-5 w-3/4 rounded mb-2" />
          <SkeletonBox className="h-4 w-2/3 rounded" />
        </div>
        <div className="w-full sm:w-auto flex gap-3">
          <SkeletonBox className="h-10 w-48 rounded-full" />
          <SkeletonBox className="h-10 w-36 rounded-full" />
        </div>
      </section>

      {/* Footer placeholders */}
      <footer className="pt-6 border-t">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((_, i) => (
            <div key={i} className="space-y-3">
              <SkeletonBox className="h-4 w-24 rounded" />
              <SkeletonBox className="h-3 w-full rounded" />
              <SkeletonBox className="h-3 w-5/6 rounded" />
              <SkeletonBox className="h-3 w-2/3 rounded" />
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center justify-between">
          <SkeletonBox className="h-4 w-40 rounded" />
          <SkeletonBox className="h-4 w-40 rounded" />
        </div>
      </footer>
    </main>
  );
};

export default LandingPageSkeleton;